import { z } from 'zod';

/**
 * Reusable Zod fragments for common environment variables across apps.
 * Use these in app-specific env.schema.ts by spreading or extending.
 *
 * All values from process.env are strings. Use z.string() for required vars.
 */

/** Required string (env vars are always string | undefined) */
export const requiredString = (key: string) =>
  z
    .string({ required_error: `${key} is required` })
    .min(1, { message: `${key} is required` });

/** Node environment */
export const nodeEnvSchema = z
  .enum([
    'development',
    'production',
    'test',
    'staging',
    'dev',
    'earth',
    'neptune',
    'pluto',
    'uranus',
    'saturn',
    'supreme',
    'onyx',
    'ushba',
    'transformers',
    'immortals',
    'argonauts',
  ])
  .optional()
  .default('development');

/** Port number - string from env, with configurable default */
export const portSchema = (defaultPort = '3000') =>
  z
    .string()
    .optional()
    .default(defaultPort)
    .refine(
      (val) => {
        const n = parseInt(val, 10);
        return !Number.isNaN(n) && n > 0 && n < 65536;
      },
      { message: 'PORT must be between 1 and 65535' },
    );

/** MongoDB connection URI - required for apps using Mongo */
export const mongodbUriSchema = z.object({
  MONGODB_URI: requiredString('MONGODB_URI'),
});

/** Redis host/port pair helper */
const redisSchema = (prefix: string) =>
  z.object({
    [`${prefix}_HOST`]: requiredString(`${prefix}_HOST`),
    [`${prefix}_PORT`]: requiredString(`${prefix}_PORT`),
  });

/** Hub Redis - required for apps using Hub Redis/BullMQ */
export const hubRedisSchema = redisSchema('HUB_REDIS');

/** User statistics Redis */
export const userStatisticsRedisSchema = redisSchema('USER_STATISTICS_REDIS');

/** Meta Trader Redis */
export const metaTraderRedisSchema = redisSchema('META_TRADER_REDIS');

/** Meta Trader Contest Redis */
export const metaTraderContestRedisSchema = redisSchema(
  'META_TRADER_CONTEST_REDIS',
);

/** Trader Evolution Redis */
export const traderEvolutionRedisSchema = redisSchema('TRADER_EVOLUTION_REDIS');

/** C-Trader Redis */
export const cTraderRedisSchema = redisSchema('C_TRADER_REDIS');

/** BlackArrow Futures Redis */
export const blackArrowFuturesRedisSchema = redisSchema(
  'BLACK_ARROW_FUTURES_REDIS',
);

/** Contest Redis */
export const contestRedisSchema = redisSchema('CONTEST_REDIS');

/** Microservice name - required for apps registered as microservices */
export const microserviceNameSchema = z.object({
  MICROSERVICE_NAME: requiredString('MICROSERVICE_NAME'),
});

/** Descope auth */
export const descopeSchema = z.object({
  DESCOPE_PROJECT_ID: requiredString('DESCOPE_PROJECT_ID'),
  DESCOPE_MANAGEMENT_KEY: requiredString('DESCOPE_MANAGEMENT_KEY'),
});

/** AWS Cognito */
export const awsCognitoSchema = z.object({
  AWS_USER_POOL_ID: requiredString('AWS_USER_POOL_ID'),
  AWS_CLIENT_ID: requiredString('AWS_CLIENT_ID'),
});

/** Charts service */
export const chartsSchema = z.object({
  CHARTS_BASE_URL: requiredString('CHARTS_BASE_URL'),
  X_API_KEY: requiredString('X_API_KEY'),
});

/** DxFeed / dxLink REST (community stats market snapshots; same token as charts-api WS when used) */
export const dxlinkSchema = z.object({
  DXLINK_REST_URL: z.string().optional(),
  DXLINK_AUTH_TOKEN: z.string().optional(),
});

/** Optional common vars that appear across many apps */
export const optionalCommonSchema = z.object({
  NODE_ENV: nodeEnvSchema,
  E2E: z.string().optional(),
  REDIS_CACHE_URL: z.string().optional(),
});

/** C-Trader Reporting DB (PostgreSQL) */
export const cTraderDbSchema = z.object({
  C_TRADER_DB_HOST: requiredString('C_TRADER_DB_HOST'),
  C_TRADER_DB_PORT: requiredString('C_TRADER_DB_PORT'),
  C_TRADER_DB_USER: requiredString('C_TRADER_DB_USER'),
  C_TRADER_DB_PASSWORD: requiredString('C_TRADER_DB_PASSWORD'),
  C_TRADER_DB_NAME: requiredString('C_TRADER_DB_NAME'),
});

/** Alpha Vantage API */
export const alphaVantageSchema = z.object({
  ALPHA_VANTAGE_API_KEY: requiredString('ALPHA_VANTAGE_API_KEY'),
});

/** OpenTelemetry + Coralogix tracing env vars (all optional) */
export const tracingEnvSchema = z.object({
  OTEL_EXPORTER_OTLP_ENDPOINT: z.string().optional(),
  OTEL_EXPORTER_OTLP_HEADERS: z.string().optional(),
  OTEL_SERVICE_NAME: z.string().optional(),
  SERVICE_VERSION: z.string().optional(),
  CX_APPLICATION_NAME: z.string().optional(),
  CX_SUBSYSTEM_NAME: z.string().optional(),
  OTEL_SAMPLING_RATIO: z
    .string()
    .regex(/^(0(\.\d+)?|1(\.0+)?)$/)
    .optional(),
});
